import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  InputAdornment,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Chip} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import { Filter } from "lucide-react";
import LayerdIcon from "../../../common/LayerdIcon";
import RefreshIconButton from "../../../common/RefreshIconButton";
import colors from "../../../../utils/colors";
import { SafeTextField } from "../../../common/Greens";
import { filterInputSx } from "../services/api_security/asecUi";

const brandBlue = colors.primary[300];
const FILTER_HEIGHT = 36;

const secondaryActionSx = {
  height: FILTER_HEIGHT,
  border: `1px solid ${colors.neutral[200]}`,
  borderRadius: "8px",
  fontSize: 14,
  color: colors.neutral[700],
  textTransform: "none",
  px: 2,
  backgroundColor: "#fff",
  "&:hover": { borderColor: colors.neutral[300], backgroundColor: colors.neutral[50] },
};

const primaryActionSx = {
  height: FILTER_HEIGHT,
  backgroundColor: brandBlue,
  border: `1px solid ${brandBlue}`,
  borderRadius: "8px",
  fontSize: 14,
  lineHeight: "21px",
  color: "#fff",
  textTransform: "none",
  px: 3,
  "&:hover": { backgroundColor: colors.primary[400], borderColor: colors.primary[400], color: "#fff" },
};

export function SettingsFilterBar({ children, actions }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        backgroundColor: "white",
        borderRadius: "8px",
        border: `1px solid ${colors.neutral[200]}`,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ gap: 1.5, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LayerdIcon icon={Filter} size={18} color="primary" />
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[800] }}>
              Filters
            </Typography>
          </Stack>
          {children}
        </Stack>
        {actions ? (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
            {actions}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}

SettingsFilterBar.propTypes = {
  children: PropTypes.node,
  actions: PropTypes.node,
};

export function SettingsSearchField({ value, onChange, placeholder, width = 240 }) {
  return (
    <SafeTextField
      size="small"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: colors.neutral[400], fontSize: 18 }} />
          </InputAdornment>
        ),
      }}
      sx={{ width, ...filterInputSx }}
    />
  );
}

SettingsSearchField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  width: PropTypes.number,
};

export function SettingsFilterMenu({
  label,
  value,
  displayValue,
  options,
  onChange,
  onClear,
  icon,
  minWidth = 160,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const hasValue = value !== "" && value != null;
  const selectedLabel = displayValue || options.find((o) => o.value === value)?.label || value;

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          height: FILTER_HEIGHT,
          borderRadius: "8px",
          border: `1px solid ${hasValue ? brandBlue : colors.neutral[200]}`,
          backgroundColor: "#fff",
          cursor: "pointer",
          minWidth,
          "&:hover": {
            borderColor: brandBlue,
            "& .filter-label": { color: brandBlue },
          },
        }}
      >
        {icon ? <LayerdIcon icon={icon} size={16} color="primary" /> : null}
        <Typography
          className="filter-label"
          sx={{
            fontSize: 14,
            color: hasValue ? brandBlue : colors.neutral[800],
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {hasValue ? selectedLabel : label}
        </Typography>
        {hasValue && onClear ? (
          <CloseIcon
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            sx={{ fontSize: 14, color: brandBlue, "&:hover": { color: colors.red[100] } }}
          />
        ) : (
          <ArrowDownIcon sx={{ fontSize: 16, color: brandBlue }} />
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${colors.neutral[200]}`,
            minWidth,
            maxHeight: 320,
            overflow: "auto",
            backgroundColor: "#fff",
          },
        }}
      >
        <List disablePadding dense>
          {options.map((option) => (
            <ListItem key={String(option.value)} disablePadding>
              <ListItemButton
                selected={option.value === value}
                onClick={() => {
                  onChange(option.value);
                  setAnchorEl(null);
                }}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  "&:hover": { backgroundColor: colors.primary.alpha },
                  "&.Mui-selected": { backgroundColor: colors.primary.alpha },
                }}
              >
                <ListItemText
                  primary={option.label}
                  secondary={option.secondary}
                  primaryTypographyProps={{ sx: { fontSize: 14, color: colors.neutral[800] } }}
                  secondaryTypographyProps={{ sx: { fontSize: 12, color: colors.neutral[600] } }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
}

SettingsFilterMenu.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  displayValue: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      secondary: PropTypes.string,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.object]),
  minWidth: PropTypes.number,
};

export function SettingsFilterRefreshButton({ onClick, disabled, loading }) {
  return <RefreshIconButton onClick={onClick} disabled={disabled} loading={loading} />;
}

SettingsFilterRefreshButton.propTypes = {
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
};

export function SettingsFilterSecondaryButton({ children, ...props }) {
  return (
    <Button {...props} sx={{ ...secondaryActionSx, ...props.sx }}>
      {children}
    </Button>
  );
}

export function SettingsFilterPrimaryButton({ children, ...props }) {
  return (
    <Button {...props} sx={{ ...primaryActionSx, ...props.sx }}>
      {children}
    </Button>
  );
}

export function SettingsFilterClearButton({ onClick, disabled, label = "Clear filters" }) {
  return (
    <Chip
      label={label}
      onClick={onClick}
      disabled={disabled}
      sx={{
        height: FILTER_HEIGHT,
        fontSize: 14,
        borderRadius: "8px",
        backgroundColor: "#fff",
        border: `1px solid ${colors.neutral[200]}`,
        color: colors.neutral[800],
        "&:hover": {
          borderColor: colors.red[100],
          color: colors.red[100],
          backgroundColor: "#fff",
        },
        "&.Mui-disabled": {
          opacity: 0.5,
        },
      }}
    />
  );
}

SettingsFilterClearButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string,
};
